/**
 * Local crash diagnostics: a small, persisted ring buffer of recent errors that a field
 * technician can export for support.
 *
 * Remote capture already exists (OpenReplay session replay + error capture in App.tsx),
 * but it uploads when connectivity allows. This buffer complements it: a local, always-
 * available record that survives a reload (localStorage) and is exportable offline, plus a
 * pluggable reporter so an additional sink can be wired later with one call.
 *
 * The service is a plain class (matching the existing service pattern, e.g.
 * observationDraftService) with an injectable clock + storage so it is unit-testable
 * without a DOM.
 */

export interface CrashRecord {
  /** epoch ms */
  time: number;
  /** where it came from: 'react.uncaught' | 'react.caught' | 'react.recoverable' | 'window.error' | 'unhandledrejection' | custom */
  kind: string;
  message: string;
  stack?: string;
  /** React component stack or any extra context */
  context?: string;
}

export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

const STORAGE_KEY = 'bdk_crash_diagnostics';

const toMessage = (error: unknown): { message: string; stack?: string } => {
  if (error instanceof Error)
    return { message: error.message, stack: error.stack };
  if (typeof error === 'string') return { message: error };
  try {
    return { message: JSON.stringify(error) };
  } catch {
    return { message: String(error) };
  }
};

export class CrashDiagnosticsService {
  private buffer: CrashRecord[] = [];
  private reporter: ((record: CrashRecord) => void) | null = null;

  constructor(
    private readonly max = 50,
    private readonly now: () => number = () => Date.now(),
    private readonly storage?: StorageLike,
  ) {
    this.buffer = this.load();
  }

  /** Record an error. Never throws (diagnostics must not cause a second failure). */
  record(kind: string, error: unknown, context?: string): CrashRecord {
    const { message, stack } = toMessage(error);
    const rec: CrashRecord = {
      time: this.now(),
      kind,
      message,
      stack,
      context,
    };
    try {
      this.buffer.push(rec);
      if (this.buffer.length > this.max) {
        this.buffer.splice(0, this.buffer.length - this.max);
      }
      this.persist();
      this.reporter?.(rec);
    } catch {
      /* diagnostics are best-effort */
    }
    return rec;
  }

  list(): CrashRecord[] {
    return [...this.buffer];
  }

  /** JSON string of the recent crashes, for a support export / clipboard. */
  export(): string {
    return JSON.stringify(this.buffer, null, 2);
  }

  clear(): void {
    this.buffer = [];
    try {
      this.storage?.removeItem(STORAGE_KEY);
    } catch {
      /* ignore */
    }
  }

  /** Wire an additional sink (e.g. a future remote endpoint). No-op by default. */
  setReporter(reporter: ((record: CrashRecord) => void) | null): void {
    this.reporter = reporter;
  }

  private load(): CrashRecord[] {
    try {
      const raw = this.storage?.getItem(STORAGE_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed)
        ? (parsed as CrashRecord[]).slice(-this.max)
        : [];
    } catch {
      return [];
    }
  }

  private persist(): void {
    try {
      this.storage?.setItem(STORAGE_KEY, JSON.stringify(this.buffer));
    } catch {
      /* storage may be full or unavailable - the in-memory buffer still works */
    }
  }
}

const browserStorage = (): StorageLike | undefined => {
  try {
    return typeof localStorage !== 'undefined' ? localStorage : undefined;
  } catch {
    return undefined;
  }
};

export const crashDiagnostics = new CrashDiagnosticsService(
  50,
  () => Date.now(),
  browserStorage(),
);
